import { BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, Branding, DisabledPrompt, useBCMSContext, useModel, useRoute, useStateManager } from "@bcm-dev/bcms-vite";
import CustomInputSelect from "components/custom-ui/CustomInputSelect";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { UIRadio } from "components/custom-ui/UIRadioGroup";
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { PrimaryButton, SecondaryButton, UICheckbox } from "components/ui";
import UITextInput from "components/ui/UITextInput";
import Classes, { Institution, Participant } from "model";
import React from "react";
import PaymentDistributionWidget from "./components/PaymentDistributionWidget";
import "./mobile.css";
import "./styles.css";


/**
 * @function ParticipantDetailsPage
 * @returns {React.Component}
 * @exported true
 * @description The main page component for the ParticipantDetailsPage.
 */
export default function ParticipantDetailsPage(props) {
  const globalController = useBCMSContext();
  const navigate = useRoute();

  BCMSRecord.globalControllerFor(Classes, globalController);

  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      // Updates the default "participant" properties
      // which represents the entire form of the page.
      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "participant", prop ], value)
        .submit(setState);
      },

      onRoutingInputSelect: value => {
        BCMSStateManagerCRUD.init(state)
        .update([ "data", "participant", "routing" ], Institution.initByID(value).routingNumber)
        .update([ "data", "participant", "institutionName" ], Institution.initByID(value).name)
        .submit(setState);
      },

      // "Save" functionality is currently disabled.
      onSave: () => {
        const nextID = Participant.nextID();
        let addedParticipant = Participant.init({ ...data.participant.record, id: nextID });
        const defaultParticipantData = BCMSDefaultModel.participant();
        const mergedTemplate = BCMSDefaultModel.mergeRecords(addedParticipant, defaultParticipantData);
      
        mergedTemplate.accountType = mergedTemplate.accountType.charAt(0).toUpperCase() + mergedTemplate.accountType.slice(1);
        mergedTemplate.status = mergedTemplate.status.charAt(0).toUpperCase() + mergedTemplate.status.slice(1);
        mergedTemplate.number = "*" + mergedTemplate.number.slice(-4);
        BCMSRecord.batch([Participant.create({ ...mergedTemplate })], globalController, Classes);

        navigate("/achParticipants", { state : { addedParticipant : mergedTemplate, participantAdded: true },  })
      }
  });

  return(
    <PageContainer className="participant-details-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  participant: Participant.init({  participantType: "standard", accountType: "Checking", status: "active", distributePayments: "false" }),
  isRequiredFieldsEntered: false,
  institutions: Institution.initAll()
});
export const getDefaultUI = () => ({
  // no ui changes for now
  accountTypeSelectIsActive: false,
  statusSelectIsActive: false,
  routingSelectIsActive: false
});

function PageContent(props) {
  const { data, ui, onChange, onRoutingInputSelect, onSave } = useStateManager();
  const navigate = useRoute();
  const participant = data.participant ?? {};
  const participantType = participant.participantType.toLowerCase();
  const accountType = participant.accountType.toLowerCase();
  const participantStatus = participant.status.toLowerCase();
  const { branding } = useModel();
  const { options } = branding;
  const showSendEmailCheckbox = options.send_email_achparticipants_abi22 === "true";
  const emailFormRowViewClasses = showSendEmailCheckbox ? { className: "email-form-row default" } : { className: "email-form-row " };

  const checkRequiredFields = () => {
    return participant.participantName && 
    participant.participantEmail && 
    participant.nickname && 
    participant.routing && 
    participant.uniqueID && 
    participant.number && 
    participant.confirmedAccountNumber
  };

  const routingSelectItems = data.institutions.reduce((acc, item) => {
    acc.push(
      <CustomSelectItem key={ item.id } value={ `${ item.id }` } dataPoint={ `Participant_Details_Routing_${ item.id }` }>{ `${ item.routingNumber }: ${ item.name }` }</CustomSelectItem>
    )
    return acc;
  }, []);

  return (
  <PageContentView>
    <CenterContentContainer>
      <ContentHeaderView text="Participant Details" />
      <FormContainer className="small-form-width">
        <Branding id="participant_type_abi22" value="true" default="true">
          <FormRowView text="Participant Type" className="participant-type-form-row">
            <div className="flex-container flex-horizontal align-center radio-container">
              <div className="flex-width">
                <UIRadio label="Standard" dataPoint={ `Participant_Details_Standard_Radio` } checked={ participantType === "standard" ? true : false } />
              </div>
              <div className="flex-width">
                {/* This radio button is not supported for now */}
                <DisabledPrompt>
                  <UIRadio label="Child Support Payment" dataPoint={ `Participant_Details_Child_Support_Radio` } checked={ participantType === "childSupportPayment" ? true : false } />
                </DisabledPrompt>
              </div>
            </div>
          </FormRowView>
        </Branding>
        <FormRowView text="* Name" className="name-form-row">
          <UITextInput value={ participant.participantName } dataPoint={ `Participant_Details_Name_Input` } onChange={ onChange("participantName") } />
        </FormRowView>
        <FormRowView text="* Email Address" { ...emailFormRowViewClasses }>
          <UITextInput value={ participant.participantEmail } dataPoint={ `Participant_Details_Email_Input` } onChange={ onChange("participantEmail") } />
        </FormRowView>
        { showSendEmailCheckbox && 
          <FormRowView className="checkbox-form-row send-email-checkbox">
            <UICheckbox label="Send email when batch status is changed to Processed" dataPoint={ `Participant_Details_Send_Email_Batch_Changed_To_Processed` } value={ participant.isCheck ? "checked" : "" } onChange={ onChange("isCheck") } />
          </FormRowView>
        }
        <FormRowView text="* Nickname" className="nickname-form-row">
          <UITextInput value={ participant.nickname } dataPoint={ `Participant_Details_Nickname_Input` } onChange={ onChange("nickname") } />
        </FormRowView>
        <FormRowView text="Institution Name" className="institution-name-form-row">
          <UITextInput value={ participant.institutionName } dataPoint={ `Participant_Details_Institution_Name_Input` } onChange={ onChange("institutionName") } />
        </FormRowView>
        <FormRowView text="* Routing Number" className="routing-number-form-row">
          <CustomInputSelect value={ participant.routing } active={ ui.routingSelectIsActive } dataPoint={ `Participant_Details_Routing_Select` }
           onChange={ onChange("routing") } onCustomInputItemSelect={ onRoutingInputSelect } >
            { routingSelectItems }
          </CustomInputSelect>
        </FormRowView>
        <FormRowView text="* Unique Identifier" className="unique identifier-form-row">
          <UITextInput value={ participant.uniqueID } dataPoint={ `Participant_Details_Unique_Identifier_Input` } onChange={ onChange("uniqueID") } />
        </FormRowView>
        <FormRowView text="* Account Number" className="account-number-form-row">
          <UITextInput value={ participant.number } dataPoint={ `Participant_Details_Account_Number_Input` } onChange={ onChange("number") } />
        </FormRowView>
        <FormRowView text="* Confirm Account Number" className="confirm-number-form-row">
          <UITextInput value={ participant.confirmedAccountNumber } dataPoint={ `Participant_Details_Confirm_Account_Number_Input` } onChange={ onChange("confirmedAccountNumber") } />
        </FormRowView>
        <FormRowView text="* Account Type" className="account-type-form-row">
          <CustomSelect value={ accountType } active={ ui.accountTypeSelectIsActive } dataPoint={ `Participant_Details_Account_Type_Select` } onChange={ onChange("accountType") }>
            <CustomSelectItem value="checking" dataPoint={ `Participant_Details_Account_Option_Checking` }>Checking</CustomSelectItem>
            <CustomSelectItem value="savings" dataPoint={ `Participant_Details_Account_Option_Savings` }>Savings</CustomSelectItem>
            <CustomSelectItem value="loan" dataPoint={ `Participant_Details_Account_Option_Loan` }>Loan</CustomSelectItem>
          </CustomSelect>
        </FormRowView>
        <FormRowView text="* Status" className="status-form-row">
          <CustomSelect value={ participantStatus } active={ ui.statusSelectIsActive } dataPoint={ `Participant_Details_Status_Select` } onChange={ onChange("status") }>
            <CustomSelectItem value="active" dataPoint={ `Participant_Details_Status_Option_Active` }>Active</CustomSelectItem>
            <CustomSelectItem value="hold" dataPoint={ `Participant_Details_Status_Option_Hold` }>Hold</CustomSelectItem>
          </CustomSelect>
        </FormRowView>
        <FormRowView text="Group" className="group-form-row">
          <UITextInput value={ participant.group } dataPoint={ `Participant_Details_Group_Input` } onChange={ onChange("group") } />
        </FormRowView>
        <FormRowView text="Discretionary Data" className="discretionary-data-form-row">
          <UITextInput value={ participant.discretionaryData } dataPoint={ `Participant_Details_Discretionary_Data_Input` } onChange={ onChange("discretionaryData") } />
        </FormRowView>
        {/* This checkbox is disabled for now */}
        <Branding id="distribute_payment_abi22" value="true" default="true">
          <FormRowView className="checkbox-form-row distribute-payments-row">
            <UICheckbox disabled={ !checkRequiredFields() } label="Distribute Payments" dataPoint={ `Participant_Details_Distribute_Payments` }
            value={ participant.distributePayments } onChange={ onChange("distributePayments") }/>
          </FormRowView>
        </Branding>

        </FormContainer>

        { participant.distributePayments && <PaymentDistributionWidget /> }

        <FormContainer className="small-form-width">
        <FormButtonRow>
          <SecondaryButton text="Cancel" dataPoint={ `Participant_Details_Cancel_Button` } onClick={() => navigate("/achparticipants")} />
          <PrimaryButton text="Save" dataPoint={ `Participant_Details_Save_Button` } onClick={ onSave } />
          { participant.distributePayments &&
          <div className="disabled-button-container">
            <DisabledPrompt>
              <PrimaryButton text="Add Distribution" dataPoint={ `Participant_Details_Add_Distribution_Button` } />
            </DisabledPrompt>
          </div>
          }
        </FormButtonRow>
      </FormContainer>
    </CenterContentContainer>
  </PageContentView>
  );
}